import type { ReactNode } from 'react';
import { Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  DisconnectOutlined,
  FireOutlined,
  InfoCircleOutlined,
  LockOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useThemeMode } from '@goelectric/ui-components';
import type { StatusIconKey } from '@goelectric/ui-components';

/**
 * Primitivas compartidas de los badges de estado (color + icono + texto).
 *
 * Las usan tanto el NOC (`features/noc/components/StatusBadge`) como Aliados
 * (`features/aliados/components/AllyBadges`). La regla de accesibilidad del design
 * system (WCAG: nunca solo color) vive acá una sola vez.
 */

/** Mapeo de la clave de icono del design system a iconos de antd. */
const STATUS_ICONS: Record<StatusIconKey, ReactNode> = {
  check: <CheckCircleOutlined />,
  bolt: <ThunderboltOutlined />,
  lock: <LockOutlined />,
  warning: <WarningOutlined />,
  offline: <DisconnectOutlined />,
  tool: <ToolOutlined />,
  info: <InfoCircleOutlined />,
  temperature: <FireOutlined />,
};

export function StatusIcon({ iconKey }: { iconKey: StatusIconKey }) {
  return <>{STATUS_ICONS[iconKey]}</>;
}

/**
 * Resuelve el color del badge según el tema resuelto. `forceDark` se usa dentro de
 * paneles oscuros donde el tema del documento puede ser claro (p. ej. el hero del NOC).
 */
export function useStatusColor(color: string, colorDark: string, forceDark = false): string {
  const { resolvedTheme } = useThemeMode();
  return forceDark || resolvedTheme === 'dark' ? colorDark : color;
}

interface StatusBadgeShellProps {
  color: string;
  icon: ReactNode;
  label: string;
  title: string;
}

/** Estructura común de los badges de estado: color + icono + texto (nunca solo color). */
export function StatusBadgeShell({ color, icon, label, title }: StatusBadgeShellProps) {
  return (
    <Tooltip title={title}>
      <span className="status-badge" style={{ color }} aria-label={title}>
        <span className="status-badge__dot" aria-hidden="true" />
        <span className="status-badge__icon" aria-hidden="true">
          {icon}
        </span>
        {label && <span className="status-badge__label">{label}</span>}
      </span>
    </Tooltip>
  );
}
